import { doc, getDoc, updateDoc } from "firebase/firestore";
import { deleteObject, getDownloadURL, getStorage, ref, uploadBytesResumable } from "firebase/storage";
import React, { useEffect, useState } from "react";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { db } from "../../firebase";
import DeleteButtonFile from "../Buttons/DeleteButtonFile";
import { showCustomToast } from "../Misc/CustomToast";
import pdfIcon from "../../images/pdf-icon-small.png";
import wordIcon from "../../images/word-icon-small.png";
import excelIcon from "../../images/excel-icon-small.png";
import zIcon from "../../images/icons/7z-small-icon.png";
import csvIcon from "../../images/icons/csv-small-icon.png";
import gifIcon from "../../images/icons/gif-small-icon.png";
import jpgIcon from "../../images/icons/jpg-small-icon.png";
import pngIcon from "../../images/icons/png-small-icon.png";
import pptIcon from "../../images/icons/ppt-small-icon.png";
import rarIcon from "../../images/icons/rar-small-icon.png";
import txtIcon from "../../images/icons/txt-small-icon.png";
import zipIcon from "../../images/icons/zip-small-icon.png";
import "./UploaderAraEncapsulated.css";

function UploaderAraEncapsulated(props) {
  const maxSizePicFile = 100 * 1024 * 1024;
  const fileField = props.fileField || "fileUrlsRoi";
  const storageFolder = props.storageFolder || "Documents/ScannedRois";
  const inputId = props.inputId || "uploadFiles";
  const uploadTitle = props.uploadTitle || "Upload scanned ROI documents";
  const uploadDescription = props.uploadDescription || "Add up to 3 files. Uploads start automatically after selection.";
  const [fileLinks, setFileLinks] = useState([]);
  const [uploadingFiles, setUploadingFiles] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const getFileIcon = (filename) => {
    const extension = filename.split(".").pop().toLowerCase();
    const icons = { pdf: pdfIcon, doc: wordIcon, docx: wordIcon, xls: excelIcon, xlsx: excelIcon, ppt: pptIcon, pptx: pptIcon, txt: txtIcon, csv: csvIcon, zip: zipIcon, rar: rarIcon, "7z": zIcon, jpg: jpgIcon, jpeg: jpgIcon, png: pngIcon, gif: gifIcon };
    return icons[extension] || pdfIcon;
  };

  const fetchFileUrls = async () => {
    const snapshot = await getDoc(doc(db, "clients", props.clientId));
    if (snapshot.exists()) setFileLinks(snapshot.data()[fileField] || []);
  };

  useEffect(() => { fetchFileUrls(); }, [props.clientId, fileField]);

  const handleFileChange = async (event) => {
    const selectedFiles = Array.from(event.target.files || []);
    if (selectedFiles.length > 3) {
      showCustomToast("You can only upload up to 3 files.", "error");
      return;
    }
    if (selectedFiles.length) await uploadFilesAndGetUrls(selectedFiles, props.clientId);
  };

  const uploadFilesAndGetUrls = async (fileList, docId) => {
    setIsLoading(true);
    const validFiles = fileList.filter((file) => {
      if (file.size > maxSizePicFile) {
        showCustomToast(`File size exceeds the limit: ${file.name}`, "error");
        return false;
      }
      return true;
    });
    setUploadingFiles(validFiles.map((file) => ({ name: file.name, progress: 0, status: "uploading" })));

    const uploads = validFiles.map((file) => new Promise((resolve, reject) => {
      const uploadTask = uploadBytesResumable(ref(getStorage(), `${storageFolder}/${docId}/${file.name}`), file);
      uploadTask.on("state_changed", (snapshot) => {
        const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        setUploadingFiles((current) => current.map((item) => item.name === file.name ? { ...item, progress, status: progress >= 100 ? "complete" : "uploading" } : item));
      }, (error) => {
        setUploadingFiles((current) => current.map((item) => item.name === file.name ? { ...item, status: "error" } : item));
        reject(error);
      }, async () => {
        try { resolve({ name: file.name, url: await getDownloadURL(uploadTask.snapshot.ref) }); }
        catch (error) { reject(error); }
      });
    }));

    try {
      const fileUrls = await Promise.all(uploads);
      const clientRef = doc(db, "clients", docId);
      const snapshot = await getDoc(clientRef);
      const existingUrls = snapshot.exists() ? snapshot.data()[fileField] || [] : [];
      await updateDoc(clientRef, { [fileField]: [...existingUrls, ...fileUrls] });
      document.getElementById(inputId).value = "";
      await fetchFileUrls();
    } catch (error) {
      console.error("Error uploading files: ", error);
      showCustomToast("Error uploading files", "error");
      setUploadingFiles((current) => current.map((file) => ({ ...file, status: "error" })));
    } finally { setIsLoading(false); }
  };

  const deleteFile = async (fileUrl, fileName, clientId) => {
    try {
      await deleteObject(ref(getStorage(), `${storageFolder}/${clientId}/${fileName}`));
      const newFileLinks = fileLinks.filter((file) => file.url !== fileUrl);
      await updateDoc(doc(db, "clients", clientId), { [fileField]: newFileLinks });
      setFileLinks(newFileLinks);
      document.getElementById(inputId).value = "";
      showCustomToast("File successfully deleted!", "success");
    } catch (error) { showCustomToast("Error deleting file", "error"); }
  };

  return (
    <div className="ara-uploader">
      <section className="ara-upload-card">
        <div className="ara-upload-heading"><div className="ara-upload-icon"><CloudUploadOutlinedIcon /></div><div><span className="ara-kicker">Secure document library</span><h3>{uploadTitle}</h3><p>{uploadDescription}</p></div></div>
        <label className="ara-dropzone" htmlFor={inputId}><input type="file" id={inputId} multiple onChange={handleFileChange} accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.zip,.rar,.7z" /><CloudUploadOutlinedIcon className="ara-dropzone-icon" /><strong>Drop files here or browse</strong><span>Images, PDF and office files · 100 MB maximum per file</span><em>Select files</em></label>
      </section>

      {uploadingFiles.length > 0 && <section className="ara-files-card"><div className="ara-section-heading"><div><span className="ara-kicker">{isLoading ? "Live upload" : "Latest upload"}</span><h3>Upload progress</h3></div><span className="ara-count">{uploadingFiles.filter((file) => file.status === "complete").length}/{uploadingFiles.length}</span></div><div className="ara-progress-list">{uploadingFiles.map((file, index) => <div className="ara-progress-item" key={`${file.name}-${index}`}><div className="ara-file-icon"><DescriptionOutlinedIcon /></div><div className="ara-file-details"><strong title={file.name}>{file.name}</strong><span>{file.status === "complete" ? "Uploaded" : file.status === "error" ? "Upload failed" : `Uploading ${Math.round(file.progress)}%`}</span><div className="ara-progress-track"><i style={{ width: `${file.progress}%` }} /></div></div>{file.status === "complete" ? <CheckCircleOutlineIcon className="ara-success" /> : file.status === "error" ? <ErrorOutlineIcon className="ara-error" /> : <b>{Math.round(file.progress)}%</b>}</div>)}</div></section>}

      <section className="ara-files-card"><div className="ara-section-heading"><div><span className="ara-kicker">Client library</span><h3>Uploaded documents</h3></div><span className="ara-count">{fileLinks.length}</span></div><div className="ara-existing-list">{fileLinks.length ? fileLinks.map((file, index) => <div key={`${file.url}-${index}`} className="ara-existing-file"><div className="ara-file-icon"><img src={getFileIcon(file.name)} alt="" /></div><a href={file.url} target="_blank" rel="noreferrer" title={file.name}>{file.name}</a><DeleteButtonFile fileName={file.name} onClick={() => deleteFile(file.url, file.name, props.clientId)} /></div>) : <div className="ara-empty">No documents uploaded yet.</div>}</div></section>
    </div>
  );
}

export default UploaderAraEncapsulated;
